import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import React, { useState } from "react";
import { fetchBrandGuidelines, regenerateImage } from "src/utils/imageGeneration";

// Aspect ratios supported by the image-generation Lambda (Gemini ImageConfig).
const ASPECT_RATIOS = ["1:1", "16:9", "9:16", "4:3", "3:4"];

const ImageWithDownload: React.FC<NodeViewProps> = ({ node, editor, getPos, deleteNode }) => {
  const [hovered, setHovered] = useState(false);
  const { src, alt, title, imageOpportunity, articleId } = node.attrs;

  // The "regenerate this image" control is only available for AI-generated
  // images that still carry their generation context (lost after a reload,
  // since markdown can't store the metadata).
  const canRegenerate = Boolean(imageOpportunity && articleId);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [comment, setComment] = useState("");
  const [aspectRatio, setAspectRatio] = useState<string>(
    imageOpportunity?.aspect_ratio || "16:9"
  );
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  const closePopover = () => {
    if (regenLoading) return;
    setAnchorEl(null);
    setRegenError(null);
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Derive clean filename: strip UUID prefix from the URL path
    // URL pattern: https://domain/{article_id}/{uuid}-{clean-name}.jpg
    const urlPath = new URL(src).pathname;
    const fullFilename = urlPath.split("/").pop() || "image.jpg";
    // UUID format: 8-4-4-4-12 hex chars + trailing hyphen
    const cleanFilename = fullFilename.replace(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/,
      ""
    );

    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cleanFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab if fetch fails (e.g., CORS)
      window.open(src, "_blank");
    }
  };

  const handleRegenerate = async () => {
    setRegenLoading(true);
    setRegenError(null);
    try {
      const brandStyleGuidelines = await fetchBrandGuidelines(articleId);
      const { imageUrl, metadata } = await regenerateImage({
        articleId,
        // Override the aspect ratio so the user can change dimensions on
        // regenerate; the backend reads aspect_ratio off the opportunity.
        opportunity: { ...imageOpportunity, aspect_ratio: aspectRatio },
        userFeedback: comment,
        brandStyleGuidelines,
        // Current image on the node — the backend passes it to Gemini so
        // the regeneration adjusts this image rather than starting over.
        previousImageUrl: src,
      });
      // Insert the regenerated image as a NEW node right after this one,
      // rather than replacing it — the user keeps the original and decides
      // which to delete. The new node carries its own opportunity + articleId
      // so it gets its own regenerate control and is persisted (keyed by src).
      const pos = typeof getPos === "function" ? getPos() : null;
      const insertAt =
        pos != null ? pos + node.nodeSize : editor.state.doc.content.size;
      editor
        .chain()
        .insertContentAt(insertAt, {
          type: "image",
          attrs: {
            src: imageUrl,
            alt: metadata?.alt_text || alt || "",
            imageOpportunity: metadata || imageOpportunity,
            articleId,
          },
        })
        .run();
      setComment("");
      setAnchorEl(null);
    } catch (err: any) {
      console.error("Single image regeneration error:", err);
      setRegenError(err?.message || "Failed to regenerate image");
    } finally {
      setRegenLoading(false);
    }
  };

  return (
    <NodeViewWrapper
      as="span"
      style={{ display: "inline-block", position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={src}
        alt={alt || ""}
        title={title || ""}
        className="w-full h-auto mx-auto my-auto"
        style={{
          maxWidth: "800px",
          height: "auto",
          margin: "0 auto",
          display: "block",
        }}
      />
      {alt && (
        <span
          style={{
            display: "block",
            textAlign: "center",
            fontSize: "12px",
            color: "#666",
            marginTop: "4px",
            fontStyle: "italic",
          }}
        >
          {alt}
        </span>
      )}
      {(hovered || Boolean(anchorEl)) && canRegenerate && (
        <Tooltip title="Regenerate this image">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 48,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
            }}
          >
            <AutoAwesomeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {hovered && (
        <IconButton
          onClick={handleDownload}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.6)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      )}
      {hovered && (
        <Tooltip title="Delete this image">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              deleteNode();
            }}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: canRegenerate ? 88 : 48,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(211,47,47,0.85)" },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // Stop ProseMirror from intercepting clicks/keys inside the popover.
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 2, width: 320, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: "#5048E5" }} fontSize="small" />
            <Typography variant="subtitle2">Regenerate image</Typography>
          </Box>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            size="small"
            placeholder="What would you like to change? e.g. warmer colors, less text, a different layout…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={regenLoading}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Aspect ratio"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={regenLoading}
          >
            {ASPECT_RATIOS.map((ratio) => (
              <MenuItem key={ratio} value={ratio}>
                {ratio}
              </MenuItem>
            ))}
          </TextField>
          {regenError && (
            <Typography variant="body2" color="error">
              {regenError}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={closePopover} size="small" disabled={regenLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleRegenerate}
              size="small"
              variant="contained"
              disabled={regenLoading}
              startIcon={
                regenLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeIcon fontSize="small" />
                )
              }
              sx={{ backgroundColor: "#5048E5", "&:hover": { backgroundColor: "#3f38c4" } }}
            >
              {regenLoading ? "Regenerating…" : "Regenerate"}
            </Button>
          </Box>
        </Box>
      </Popover>
    </NodeViewWrapper>
  );
};

export default ImageWithDownload;
